'use client'

import React, { useEffect, useState } from 'react';
import { useAOS } from '../../../../lib/hooks/useAOS';
import useInView from '../../../../lib/hooks/useInView';
import styles from './StatsSection.module.scss';

interface StatItem {
    label: string;
    count: number;
    link?: string;
    percentage?: number;
    icon?: string;
    hoverText?: string;
}

interface StatsSectionProps {
    title: string;
    stats: StatItem[];
    theme?: 'light' | 'dark';
    disableAnimation?: boolean;
}

const StatsSection: React.FC<StatsSectionProps> = ({ title, stats, theme = 'dark', disableAnimation = false }) => {
    const [ref, isInView] = useInView();
    useAOS();
    const [displayCounts, setDisplayCounts] = useState<number[]>(stats.map(() => 0));
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    const [animate, setAnimate] = useState(false);

    useEffect(() => {
        setAnimate(true);
        const timeout = setTimeout(() => setAnimate(false), 600);
        return () => clearTimeout(timeout);
    }, [activeIndex]);

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveIndex((prevIndex) => (prevIndex + 1) % stats.length);
        }, 2000);
        return () => clearInterval(interval);
    }, [stats.length]);

    useEffect(() => {
        if ((isInView || disableAnimation) && !isMobile) {
            if (disableAnimation) {
                setDisplayCounts(stats.map(stat => stat.count));
                return;
            }

            const intervals = stats.map((stat, index) => {
                const increment = Math.ceil(stat.count / 60);
                return setInterval(() => {
                    setDisplayCounts(prev => {
                        const newCounts = [...prev];
                        newCounts[index] = Math.min(stat.count, newCounts[index] + increment);
                        return newCounts;
                    });
                }, 30);
            });
            return () => intervals.forEach(clearInterval);
        }
    }, [isInView, stats, disableAnimation, isMobile]);

    return (
        <section
            className={`${styles.statsSection} ${theme === 'light' ? styles.light : styles.dark}`}
            ref={ref}
            data-aos="fade-up"
        >
            <h2 className={styles.title} data-aos="fade-right">{title}</h2>
            <div className={styles.bigNumberWrapper}>
                <div className={styles.fissureBg} aria-hidden />
                <span className={styles.bigNumber} data-aos="zoom-in">
                    {stats[activeIndex].count.toLocaleString()}
                </span>
            </div>

            <p className={styles.subText} data-aos="fade-right">{stats[activeIndex].label}</p>

            <div className={styles.statsGrid} data-aos="fade-up">
                {stats.map((stat, i) => (
                    <div
                        key={i}
                        className={`${styles.statBlock} ${i === activeIndex ? styles.active : ''}`}
                    >
                        {stat.percentage !== undefined && (
                            <svg role="img" aria-label={`${stat.label} percentage circle`} className={styles.circle} viewBox="0 0 36 36">
                                <path className={styles.bg} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                <path className={styles.progress} strokeDasharray={`${stat.percentage}, 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                            </svg>
                        )}
                        <div className={styles.labelWrapper}>
                            <h4 className={styles.label}>
                                {stat.icon && <img src={stat.icon} alt={`${stat.label} icon`} />}
                                {stat.link ? <a href={stat.link}>{stat.label}</a> : stat.label}
                            </h4>
                            <h3 className={styles.count}>
                                {displayCounts[i].toLocaleString()}
                            </h3>
                        </div>
                    </div>
                ))}
            </div>
            <p data-aos="fade">{stats[activeIndex].hoverText}</p>
        </section>
    );
};

export default StatsSection;
